import {
    IApplyQuestion,
    IBaseServerJobAttribute,
    IBaseServerJobBenefits,
    IBaseServerJobRequirement,
    IBaseServerJobRequirementGroup,
    ICandidateJob,
    IClientJobCandidatesStatistics,
    IClientJobStatistics,
    IJob,
    IJobsBoardFilter,
    IJobsBoardForm,
    IJobStep,
    IJobQuestionObject,
    IServerApplyQuestion,
    IServerApplyQuestionResponse,
    ISaveJobPayload,
    IServerCandidateJob,
    IServerJob,
    IServerJobCandidatesStatistics,
    IServerJobRequirementOption,
    IServerJobStatistics,
    ITrackableSource,
    ITrackableSourceObject,
    IServerJobSteps,
    IServerUser,
    JobAttribute,
    jobsBoardFiltersInitState,
    IServerJobRequirementRecommendation,
    RequirementLevels,
    ISaveJobRequirement,
    IServerJobApplication,
    IServerJobCandidate,
    IJobCandidatesByStep,
    IJobStepCandidate,
    QuestionType,
    normalizeRequirementRange,
} from '../../../common';
import { getUserFromServerUserFormat } from '../../user/saga/manager';

export const removeEmptyTags = (html: string): string => {
    if (!html) return '';

    // remove tags with nothing between them (like <p></p>)
    let result = html.replace(/<([a-z][a-z0-9]*)[^>]*>(\s*)<\/\1>/gi, '$2');

    // remove tags with only &nbsp; or <br> between them
    result = result.replace(
        /<([a-z][a-z0-9]*)[^>]*>(\s*(&nbsp;|\s|<br\s*\/?>)\s*)<\/\1>/gi,
        '$2'
    );

    // remove standalone <br> tags
    result = result.replace(/<br\s*\/?>/gi, '');

    // If we made changes, run again to catch nested empty tags
    if (result !== html) {
        return removeEmptyTags(result);
    }

    return result;
};

export const cleanJobHtmlFields = (job: ISaveJobPayload): ISaveJobPayload => {
    if (!job) return job;
    const cleanedJob = { ...job };
    if (cleanedJob.company_overview) {
        cleanedJob.company_overview = removeEmptyTags(
            cleanedJob.company_overview
        );
    }
    if (cleanedJob.responsibilities) {
        cleanedJob.responsibilities = removeEmptyTags(
            cleanedJob.responsibilities
        );
    }
    if (cleanedJob.description) {
        cleanedJob.description = removeEmptyTags(cleanedJob.description);
    }
    return cleanedJob;
};

export const getJobStepsFromServerFormat = (
    steps: Array<IServerJobSteps>,
    recruiter: IServerUser,
    creator: IServerUser
): Array<IJobStep> => {
    return steps.map((step, index) => {
        const stepOwner = step.step_owner || recruiter || creator;
        const ownerName = `${stepOwner.first_name} ${stepOwner.last_name}`;
        const ownerId = String(stepOwner.id);

        return {
            id: step.id,
            name: step.name,
            stepOwner: {
                label: ownerName,
                value: ownerId,
            },
            isFirstStep: index === 0,
            isLastStep: index === steps.length - 1,
            interviewers: (step.interviewers || []).map((interviewer) => ({
                label: `${interviewer.first_name} ${interviewer.last_name}`,
                value: String(interviewer.id),
            })),
        };
    });
};

/**
 * Formats apply questions from server response to client format.
 */
export const getApplyQuestionsFromServerFormat = (
    serverQuestions: Array<IServerApplyQuestionResponse> | undefined
): Array<IApplyQuestion> | undefined => {
    if (!serverQuestions || serverQuestions.length === 0) return undefined;

    return serverQuestions.map((question) => {
        // Legacy rows may still use TRUE_FALSE; normalize to BOOLEAN
        const normalizedType =
            (question.type as string) === 'TRUE_FALSE'
                ? QuestionType.BOOLEAN
                : question.type;

        return {
            id: question.id,
            jobId: question.job,
            companyId: question.company,
            question: question.question,
            type: normalizedType,
            index: question.index,
            isRequired: question.is_required ?? false,
            isDealbreaker: question.is_dealbreaker ?? false,
            options: (question.options || []).map((opt) => ({
                value: String(opt.value ?? ''),
                label: opt.label ?? '',
                key: String(opt.value ?? ''),
                rangeInd: opt.range_ind ?? false,
                rangeFrom: opt.range_from ?? null,
                rangeTo: opt.range_to ?? null,
                isExpected: Boolean(opt.is_expected),
            })),
            dealbreakerExpectedAnswer: question.dealbreaker_expected_answer,
            dealbreakerExpectedOptionValues:
                question.dealbreaker_expected_options?.map((opt) =>
                    String(opt.value)
                ) ??
                (question.options || [])
                    .filter((opt) => opt.is_expected)
                    .map((opt) => String(opt.value)),
        };
    });
};

export const getJobFromServerJobFormat = (serverJob: IServerJob): IJob => {
    return {
        id: serverJob.id,
        createdAt: serverJob.created_at,
        updatedAt: serverJob.updated_at,
        companyOverview: serverJob.company_overview,
        description: serverJob.description,
        externalJobId: serverJob.external_job_id,
        location: serverJob.location,
        publishedDate: serverJob.published_date,
        responsibilities: serverJob.responsibilities,
        status: serverJob.status,
        title: serverJob.title,
        recruiterName: serverJob.recruiter?.first_name,
        creator:
            serverJob.creator && getUserFromServerUserFormat(serverJob.creator),
        recruiter:
            serverJob.recruiter &&
            getUserFromServerUserFormat(serverJob.recruiter),
        recruitingManager:
            serverJob.recruiting_manager &&
            getUserFromServerUserFormat(serverJob.recruiting_manager),
        jobAttributes: getJobAttributesFromServerFormat(
            serverJob.job_attributes
        ),
        jobBenefits: getJobBenefitsFromServerFormat(serverJob.job_benefits),
        jobRequirementGroups: getJobRequirementsGroupsFromServerFormat(
            serverJob.job_requirement_groups
        ),
        jobLanguage: serverJob.job_language,
        companyLogoUrl: serverJob.company_logo_url,
        statistics: serverJob.statistics
            ? getJobStatisticsFromServerFormat(serverJob.statistics)
            : undefined,
        steps: serverJob.steps
            ? getJobStepsFromServerFormat(
                  serverJob.steps,
                  serverJob.recruiter,
                  serverJob.creator
              )
            : undefined,
        applyQuestions: getApplyQuestionsFromServerFormat(
            serverJob.apply_questions
        ),
        requirementRecommendations: serverJob.requirement_recommendations,
        exhaustedRecommendations: serverJob.exhausted_recommendations ?? false,
        tags: serverJob.tags ?? [],
    };
};

export const getJobAttributesFromServerFormat = (
    serverJobAttributes: Array<IBaseServerJobAttribute>
) => {
    return serverJobAttributes
        ? serverJobAttributes.map((attribute) => ({
              jobAttributeOption: {
                  id: attribute.job_attribute_option.id,
                  name: attribute.job_attribute_option.name,
              },
              jobAttributeValue: {
                  id: attribute.job_attribute_value.id,
                  name: attribute.job_attribute_value.name,
              },
          }))
        : [];
};

export const getJobBenefitsFromServerFormat = (
    serverJobBenefits: Array<IBaseServerJobBenefits>
) => {
    return serverJobBenefits
        ? serverJobBenefits.map((benefit) => ({
              jobBenefitsOption: {
                  id: benefit.job_benefit_option.id,
                  name: benefit.job_benefit_option.name,
              },
              jobBenefitsValue: {
                  name: benefit.job_benefit_value?.name,
              },
          }))
        : [];
};

export const getJobRequirementsGroupsFromServerFormat = (
    serverJobRequirementGroups: Array<IBaseServerJobRequirementGroup>
) => {
    return serverJobRequirementGroups
        ? serverJobRequirementGroups.map((group) => ({
              id: group.id,
              groupName: group.group_name,
              requirements: group.requirement_options.map(
                  (requirement: IBaseServerJobRequirement) => ({
                      id: requirement.id,
                      optionId: requirement.job_requirement_option.id,
                      optionName: requirement.job_requirement_option.name,
                      importanceLevel: requirement.importance_level,
                      weight: requirement.weight,
                      rangeFrom: normalizeRequirementRange(
                          requirement.range_from
                      ),
                      rangeTo: normalizeRequirementRange(requirement.range_to),
                      name: requirement.job_requirement_option.name,
                      isVisible: requirement.is_visible,
                      rangeInd: true,
                      isAIRecommendation: requirement.is_recommended,
                  })
              ),
          }))
        : [];
};

export const getJobStatisticsFromServerFormat = (
    serverJobStatistics: IServerJobCandidatesStatistics
): IClientJobCandidatesStatistics => {
    return {
        totalApplicants: serverJobStatistics?.total_applicants,
        newApplicants: serverJobStatistics?.new_applicants,
        proceededApplicants: serverJobStatistics?.proceeded_applicants,
        declinedApplicants: serverJobStatistics?.rejected_applicants,
    };
};

export const formatJobsPageStatistics = (
    serverJobStatistics: IServerJobStatistics
): IClientJobStatistics => {
    return {
        totalApplicants: serverJobStatistics.total_applicants,
        totalDifference: serverJobStatistics.total_difference,
        newApplicants: serverJobStatistics.new_applicants,
        newDifference: serverJobStatistics.new_difference,
        proceededApplicants: serverJobStatistics.proceeded_applicants,
        proceededDifference: serverJobStatistics.proceeded_difference,
        averageJobPublish: serverJobStatistics.average_job_publish,
        rejectedApplicants: serverJobStatistics.rejected_applicants,
        rejectedDifference: serverJobStatistics.rejected_difference,
    };
};

export const formatCandidateJobFromServerFormat = (
    serverCandidateJob: Array<IServerCandidateJob>
): Array<ICandidateJob> => {
    return serverCandidateJob?.map((job) => ({
        id: job.id,
        title: job.title,
        description: job.description,
        location: job.location,
        publishedDate: job.published_date,
        externalJobId: job.external_job_id,
        jobAttributes: getJobAttributesFromServerFormat(job.job_attributes),
    }));
};

const formatFilterOptions = (
    values: string[]
): Array<{ label: string; value: string }> => {
    return [...new Set(values)].map((value) => ({
        label: value,
        value: value,
    }));
};

const updateFilterOptions = (
    filters: Array<IJobsBoardFilter>,
    filterName: string,
    values: string[]
): Array<IJobsBoardFilter> => {
    const updatedFilters = [...filters];
    const filterIndex = updatedFilters.findIndex(
        (filter) => filter.name === filterName
    );
    if (filterIndex !== -1) {
        updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            options: formatFilterOptions(values),
        };
    }
    return updatedFilters;
};

const updateFilterVisibility = (
    filters: Array<IJobsBoardFilter>,
    filterName: string,
    shouldShow: boolean
): Array<IJobsBoardFilter> => {
    const updatedFilters = [...filters];
    const filterIndex = updatedFilters.findIndex(
        (filter) => filter.name === filterName
    );
    if (filterIndex !== -1) {
        updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            shouldShow,
        };
    }
    return updatedFilters;
};

const getOptionsFromServerFormat = (
    serverCandidateJobs: Array<IServerCandidateJob>,
    attributeName: string
) => {
    return [
        ...new Set(
            serverCandidateJobs
                .flatMap((job) =>
                    job.job_attributes
                        .filter(
                            (attribute) =>
                                attribute.job_attribute_option.name ===
                                attributeName
                        )
                        .map((attribute) => attribute.job_attribute_value.name)
                )
                .filter(Boolean)
        ),
    ];
};

export const formatJobsBoardFilters = (
    serverCandidateJobs: Array<IServerCandidateJob>
): Array<IJobsBoardFilter> => {
    let filters = jobsBoardFiltersInitState;
    const jobTypes = getOptionsFromServerFormat(
        serverCandidateJobs,
        JobAttribute.JobType
    );
    const employmentTypes = getOptionsFromServerFormat(
        serverCandidateJobs,
        JobAttribute.EmploymentType
    );
    const employmentPeriods = getOptionsFromServerFormat(
        serverCandidateJobs,
        JobAttribute.EmploymentPeriod
    );
    const employmentModels = getOptionsFromServerFormat(
        serverCandidateJobs,
        JobAttribute.EmploymentModel
    );

    const allLocations = serverCandidateJobs.map((job) => job.location);

    // Filter out 'remote', null, and undefined from locations list
    const locations = allLocations.filter(
        (location) => location && location.toLowerCase() !== 'remote'
    );

    if (jobTypes.length > 0) {
        filters = updateFilterOptions(filters, 'jobTypes', jobTypes);
        filters = updateFilterVisibility(filters, 'jobTypes', true);
    }

    if (locations.length > 0) {
        filters = updateFilterOptions(filters, 'location', locations);
        filters = updateFilterVisibility(filters, 'location', true);
    }

    if (
        employmentModels.filter(
            (employmentModel) => employmentModel === 'Remote'
        )
    ) {
        filters = updateFilterVisibility(filters, 'isRemote', true);
    }

    if (
        employmentModels.filter(
            (employmentModel) => employmentModel === 'Hybrid'
        )
    ) {
        filters = updateFilterVisibility(filters, 'isHybrid', true);
    }

    return filters;
};

export const formatFiltersToQueryParams = (
    filters: IJobsBoardForm | undefined
) => {
    if (!filters) return {};

    const queryParams: Record<string, string> = {};
    const jobAttributes: Array<string> = [];
    const locations: Array<string> = [];

    if (filters.jobTypes) {
        Object.entries(filters.jobTypes).forEach(([key, value]) => {
            if (value === true) {
                jobAttributes.push(encodeURIComponent(key));
            }
        });
    }

    if (filters.isRemote) {
        jobAttributes.push('Remote');
    }
    if (filters.isHybrid) {
        jobAttributes.push('Hybrid');
    }
    if (filters.location) {
        Object.entries(filters.location).forEach(([key, value]) => {
            if (value === true) {
                locations.push(decodeURIComponent(key));
            }
        });
    }
    if (filters.search) {
        queryParams.search = filters.search;
    }
    if (jobAttributes.length > 0) {
        queryParams.job_attribute = jobAttributes.join(',');
    }
    if (locations.length > 0) {
        queryParams.location = locations.join(',');
    }

    return queryParams;
};

export const getJobRequirementsOptionsFromServerFormat = (
    serverAttributsOptions: Array<IServerJobRequirementOption>
) => {
    return serverAttributsOptions.map((option) => ({
        id: option.id,
        name: option.name,
        rangeInd: true,
        groupName: option.group_name,
    }));
};

export const formatTrackableSourceLinksToObject = (
    trackableSources: Array<ITrackableSource>
): ITrackableSourceObject => {
    return trackableSources.reduce((acc, source) => {
        acc[source.name] = source.uuid;
        return acc;
    }, {} as ITrackableSourceObject);
};

export const formatActiveJobWithSteps = (
    serverJob: IServerJob,
    activeJob: IJob
): IJob => {
    return {
        ...activeJob,
        steps: serverJob.steps
            ? getJobStepsFromServerFormat(
                  serverJob.steps,
                  serverJob.recruiter,
                  serverJob.creator
              )
            : undefined,
    };
};

export const formatJobRequirementsRecomendationsToSaveJobRequirement = (
    serverJobRequirementsRecomendations:
        | Array<IServerJobRequirementRecommendation>
        | null
        | undefined
): Array<ISaveJobRequirement> => {
    if (
        !serverJobRequirementsRecomendations ||
        !Array.isArray(serverJobRequirementsRecomendations)
    ) {
        return [];
    }

    return serverJobRequirementsRecomendations.map((recommendation, index) => {
        const rangeFrom = normalizeRequirementRange(recommendation.range_from);
        const rangeTo = normalizeRequirementRange(recommendation.range_to);

        return {
            requirementId: recommendation.job_requirement_option || null,
            recommendationId: recommendation.id || null,
            isAIRecommendation: true,
            name: recommendation.name,
            requirementGroup: recommendation.group_name,
            importanceLevel: RequirementLevels.regular,
            weight: { from: 0, to: 0 },
            rangeFrom,
            rangeTo,
            isVisible: true,
            rangeInd: true,
            requirementIndex: index,
        };
    });
};

export const formatPublishedJobsFromServerFormat = (
    serverJobs: Array<IServerJob>
): Array<{
    id: number;
    title: string;
    applyQuestions: Array<IApplyQuestion>;
}> => {
    return serverJobs.map((job) => ({
        id: Number(job.id),
        title: job.title,
        applyQuestions:
            getApplyQuestionsFromServerFormat(job.apply_questions) || [],
    }));
};

export const formatActiveJobCandidatesFromServerFormat = (
    serverActiveJobCandidates: Array<IServerJobCandidate>
): Array<IJobCandidatesByStep> => {
    // Group candidates by step
    const stepMap = new Map<string, IJobCandidatesByStep>();

    serverActiveJobCandidates.forEach((item) => {
        const stepKey = item.current_step_name;

        if (!stepMap.has(stepKey)) {
            stepMap.set(stepKey, {
                stepId: item.current_step,
                stepName: item.current_step_name,
                stepIndex: item.current_step_index,
                candidates: [],
            });
        }

        const stepData = stepMap.get(stepKey)!;
        stepData.candidates.push({
            id: item.id,
            candidateId: item.id,
            candidateName: `${item.first_name} ${item.last_name}`,
            candidateScore: item.candidate_score,
            tags: item.tags,
        });
    });

    // Convert map to array and sort by step index
    const groupedSteps = Array.from(stepMap.values()).sort(
        (a, b) => a.stepIndex - b.stepIndex
    );

    return groupedSteps;
};

export const mergeJobStepsWithCandidates = (
    jobSteps: Array<IJobStep>,
    serverCandidates: Array<IServerJobCandidate>
): Array<IJobCandidatesByStep> => {
    // Create a map of candidates by step index (current_step_index - 1)
    const candidatesByStepIndex = new Map<number, Array<IJobStepCandidate>>();

    // Group candidates by their current_step_index - 1 to match 0-based array index
    serverCandidates.forEach((candidate) => {
        const stepIndex = candidate.current_step_index - 1; // Convert from 1-based to 0-based
        if (!candidatesByStepIndex.has(stepIndex)) {
            candidatesByStepIndex.set(stepIndex, []);
        }

        candidatesByStepIndex.get(stepIndex)!.push({
            id: candidate.id,
            candidateId: candidate.id,
            candidateName: `${candidate.first_name} ${candidate.last_name}`,
            candidateScore: candidate.candidate_score,
            tags: candidate.tags,
        });
    });

    // Create result based on job steps order
    const result = jobSteps.map((step, index) => {
        const candidatesForStep = candidatesByStepIndex.get(index) || [];
        return {
            stepId: Number(step.id),
            stepName: step.name,
            stepIndex: index,
            candidates: candidatesForStep,
        };
    });

    return result;
};

export const convertJobApplicationToCandidate = (
    jobApplication: IServerJobApplication
): IServerJobCandidate => {
    const currentStepId = jobApplication.current_step;
    const steps = jobApplication.steps;
    const currentStepData = steps.find(
        (step) => Number(step.id) === currentStepId
    );
    const currentStepIndex =
        steps.findIndex((step) => Number(step.id) === currentStepId) + 1; // Convert to 1-based

    return {
        id: jobApplication.id,
        first_name: jobApplication.first_name,
        last_name: jobApplication.last_name,
        email: jobApplication.email,
        image: null,
        job: {
            id: Number(jobApplication.job.id),
            title: jobApplication.job.title,
        },
        status: jobApplication.status,
        submission_date: jobApplication.submission_date,
        candidate_score: jobApplication.candidate_score,
        current_step: currentStepId,
        current_step_name: currentStepData?.name || 'Unknown',
        current_step_status: currentStepData?.status || 'PROGRESS',
        current_step_index: currentStepIndex,
        current_step_owner: currentStepData?.users?.[0]?.id
            ? Number(currentStepData.users[0].id)
            : null,
        is_saved: jobApplication.is_saved,
        reject_reason: null,
        tags: jobApplication.tags || [],
    };
};

/**
 * Updates a candidate in the activeJobCandidates array based on server response
 * Handles moving candidate between steps, updating tags, scores, etc.
 */
export const updateCandidateInJobSteps = (
    currentCandidatesByStep: Array<IJobCandidatesByStep>,
    updatedServerCandidate: IServerJobCandidate,
    rejectedStepName: string,
    withdrawnStepName: string
): Array<IJobCandidatesByStep> => {
    const candidateId = updatedServerCandidate.id;

    // Create updated candidate in client format
    const updatedCandidate: IJobStepCandidate = {
        id: updatedServerCandidate.id,
        candidateId: updatedServerCandidate.id,
        candidateName: `${updatedServerCandidate.first_name} ${updatedServerCandidate.last_name}`,
        candidateScore: updatedServerCandidate.candidate_score,
        tags: updatedServerCandidate.tags,
    };

    // Determine target step index based on status
    let targetStepIndex: number;

    // Check if current_step_index is undefined or invalid
    if (
        updatedServerCandidate.current_step_index === undefined ||
        updatedServerCandidate.current_step_index === null ||
        isNaN(updatedServerCandidate.current_step_index)
    ) {
        // Don't move the candidate if we don't know where to put them
        return currentCandidatesByStep;
    }

    if (updatedServerCandidate.current_step_status === 'REJECT') {
        targetStepIndex = currentCandidatesByStep.findIndex(
            (step) => step.stepName === rejectedStepName
        );
        if (targetStepIndex === -1) {
            targetStepIndex = currentCandidatesByStep.length - 1;
        }
    } else if (updatedServerCandidate.current_step_status === 'WITHDRAW') {
        targetStepIndex = currentCandidatesByStep.findIndex(
            (step) => step.stepName === withdrawnStepName
        );
        if (targetStepIndex === -1) {
            targetStepIndex = currentCandidatesByStep.length - 1;
        }
    } else {
        // Use current_step_index - 1 to convert from 1-based to 0-based
        targetStepIndex = updatedServerCandidate.current_step_index - 1;
    }

    // Create new array with updated candidate
    const newCandidatesByStep = currentCandidatesByStep.map((step, index) => {
        // Remove candidate from all steps
        const filteredCandidates = step.candidates.filter(
            (c) => c.id !== candidateId
        );

        // Add candidate to target step
        if (index === targetStepIndex) {
            return {
                ...step,
                candidates: [...filteredCandidates, updatedCandidate],
            };
        }

        return {
            ...step,
            candidates: filteredCandidates,
        };
    });

    return newCandidatesByStep;
};

const SELECT_QUESTION_TYPES = [
    QuestionType.SINGLE_SELECT,
    QuestionType.MULTIPLE_SELECT,
];

const BAND_QUESTION_TYPES = [
    QuestionType.NUMERIC,
    QuestionType.SELECT_DATE,
    QuestionType.DATE_RANGE,
];

const parseBandDate = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatBandDate = (value: Date | null | undefined): string | null => {
    if (!value) return null;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formats apply questions from client form format to server format.
 * This function is used for both create and edit operations.
 * @param questions - Raw questions data from the form
 * @returns Formatted questions for the server
 */
export const formatApplyQuestionsForServer = (
    questions: Array<IJobQuestionObject> | undefined
): Array<IServerApplyQuestion> => {
    if (!questions || questions.length === 0) return [];

    return questions.map((question, arrayIndex) => {
        const questionType =
            (question.questionType?.value as QuestionType) ||
            QuestionType.SHORT_TEXT;

        const serverQuestion: IServerApplyQuestion = {
            id: question.id,
            index: arrayIndex + 1,
            question: question.questionTitle,
            type: questionType,
            is_required: question.isRequired,
            is_dealbreaker: question.isDealbreaker,
        };

        const options: NonNullable<IServerApplyQuestion['options']> = [];

        if (SELECT_QUESTION_TYPES.includes(questionType)) {
            (question.options || []).forEach((opt) => {
                options.push({
                    value: opt.value,
                    label: opt.label,
                    is_expected: Boolean(
                        question.isDealbreaker && opt.isCorrectAnswer
                    ),
                });
            });
            (question.deletedOptions || []).forEach((opt) => {
                options.push({
                    value: opt.value,
                    is_deleted: true,
                });
            });

            if (question.isDealbreaker && question.dealbreakerAnswer) {
                serverQuestion.dealbreaker_expected_option_values =
                    question.dealbreakerAnswer.split(',').filter(Boolean);
            }
        }

        if (questionType === QuestionType.BOOLEAN && question.isDealbreaker) {
            if (question.dealbreakerAnswer) {
                serverQuestion.dealbreaker_expected_answer =
                    question.dealbreakerAnswer;
            }
        }

        if (BAND_QUESTION_TYPES.includes(questionType)) {
            let bandFrom = question.bandFrom || null;
            let bandTo = question.bandTo || null;

            if (
                questionType === QuestionType.SELECT_DATE ||
                questionType === QuestionType.DATE_RANGE
            ) {
                bandFrom = formatBandDate(question.dateFrom) || bandFrom;
                bandTo = formatBandDate(question.dateTo) || bandTo;
            }

            const hasBand =
                question.isDealbreaker &&
                !!bandFrom &&
                !!bandTo;

            if (hasBand) {
                options.push({
                    value:
                        question.bandOptionValue ||
                        `new_band_${Date.now()}_${arrayIndex}`,
                    label: '',
                    range_ind: true,
                    range_from: bandFrom,
                    range_to: bandTo,
                });
            } else if (question.bandOptionValue) {
                options.push({
                    value: question.bandOptionValue,
                    is_deleted: true,
                });
            }
        }

        if (options.length > 0) {
            serverQuestion.options = options;
        }

        return serverQuestion;
    });
};

/**
 * Formats apply questions from client/redux format to form format.
 * This function is used when populating the form for editing.
 * @param questions - Apply questions from the job object
 * @param getQuestionTypeLabel - Function to get the label for a question type
 * @returns Formatted questions for the form
 */
export const formatApplyQuestionsForForm = (
    questions: Array<IApplyQuestion> | undefined,
    getQuestionTypeLabel: (type: QuestionType) => string
): Array<IJobQuestionObject> => {
    if (!questions || questions.length === 0) return [];

    return questions.map((question) => {
        const bandOption = question.options?.find((opt) => opt.rangeInd);
        const choiceOptions = (question.options || []).filter(
            (opt) => !opt.rangeInd
        );

        const bandFrom = bandOption?.rangeFrom || null;
        const bandTo = bandOption?.rangeTo || null;
        const isDateType =
            question.type === QuestionType.SELECT_DATE ||
            question.type === QuestionType.DATE_RANGE;

        let dealbreakerAnswer: string | null = null;
        if (question.isDealbreaker) {
            if (question.dealbreakerExpectedAnswer) {
                dealbreakerAnswer = question.dealbreakerExpectedAnswer;
            } else if (question.dealbreakerExpectedOptionValues?.length) {
                dealbreakerAnswer =
                    question.dealbreakerExpectedOptionValues.join(',');
            }
        }

        return {
            id: String(question.id),
            questionTitle: question.question,
            questionType: {
                value: question.type,
                label: getQuestionTypeLabel(question.type),
                key: question.type,
            },
            isRequired: question.isRequired,
            isDealbreaker: question.isDealbreaker,
            isDateRange: question.type === QuestionType.DATE_RANGE,
            options: choiceOptions.map((opt) => ({
                value: String(opt.value),
                label: opt.label,
                key: String(opt.value),
                isCorrectAnswer:
                    question.dealbreakerExpectedOptionValues?.includes(
                        String(opt.value)
                    ) || false,
            })),
            deletedOptions: [],
            dealbreakerAnswer,
            bandFrom: isDateType ? null : bandFrom,
            bandTo: isDateType ? null : bandTo,
            bandOptionValue: bandOption?.value
                ? String(bandOption.value)
                : null,
            dateFrom: isDateType ? parseBandDate(bandFrom) : null,
            dateTo: isDateType ? parseBandDate(bandTo) : null,
        };
    });
};
